import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tag, Copy, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import useSeo from "../hooks/useSeo";
import { formatDiscount, formatValidUntil } from "../components/OfferPopup";

/** Same maths as App\Models\Coupon::computeDiscount on the server. */
function priceAfter(coupon, pkg) {
    const base = Number(pkg.effective_price ?? pkg.price ?? 0);
    const applies =
        !coupon.applicable_package_ids || coupon.applicable_package_ids.includes(pkg.id);
    if (!applies) return { base, final: base, applies: false };
    const discount =
        coupon.type === "percent"
            ? base * (Math.min(Number(coupon.value), 100) / 100)
            : Math.min(Number(coupon.value), base);
    return { base, final: Math.max(0, base - Math.round(discount * 100) / 100), applies: true };
}

/** Current offer: the active coupon, and what each package costs with it. */
export default function Offer() {
    const nav = useNavigate();
    const [coupon, setCoupon] = useState(undefined); // undefined = loading
    const [packages, setPackages] = useState([]);

    useSeo({ path: "/offer" });

    useEffect(() => {
        api.get("/coupons/active")
            .then(({ data }) => setCoupon(data.coupon || null))
            .catch(() => setCoupon(null));
        api.get("/packages")
            .then(({ data }) => setPackages((data || []).filter((p) => !p.is_trial)))
            .catch(() => setPackages([]));
    }, []);

    const copyCode = () => {
        navigator.clipboard?.writeText(coupon.code);
        toast.success(`Code "${coupon.code}" copied!`);
    };

    const claim = () => {
        // Pricing.jsx picks this up and pre-fills + auto-applies the code.
        sessionStorage.setItem("pending_coupon_code", coupon.code);
        nav("/pricing");
    };

    const validUntil = coupon ? formatValidUntil(coupon.valid_until) : null;

    return (
        <div className="max-w-5xl mx-auto px-6 py-12" data-testid="offer-page">
            <div className="text-xs font-mono uppercase tracking-widest text-pink-600 font-bold mb-2">
                ◆ Offers
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                IDCardGen offers &amp; coupon codes
            </h1>

            {coupon === undefined ? (
                <div className="mt-8 text-zinc-500">Loading the current offer…</div>
            ) : coupon ? (
                <div className="mt-8 grid md:grid-cols-5 gap-6 items-stretch">
                    <div className="md:col-span-2 relative g-sunset text-white p-8 text-center overflow-hidden shadow-xl">
                        <Sparkle size={24} weight="fill" className="mx-auto mb-2 text-amber-200" />
                        <div className="text-xs font-mono uppercase tracking-widest text-white/80">
                            Limited-time offer
                        </div>
                        <div className="font-display font-black text-6xl tracking-tighter mt-1">
                            {formatDiscount(coupon)}
                        </div>
                        <p className="text-sm text-white/90 mt-2">
                            {coupon.applicable_package_ids ? "On selected packages" : "On every credit package"}
                        </p>
                        {validUntil && <p className="text-xs text-white/80 mt-3">Valid until {validUntil}</p>}
                    </div>
                    <div className="md:col-span-3 border border-zinc-200 bg-white p-6 flex flex-col justify-center gap-4">
                        <div className="text-sm text-zinc-600">Use this code at checkout:</div>
                        <button
                            onClick={copyCode}
                            className="w-full flex items-center justify-between gap-3 border-2 border-dashed border-zinc-300 hover:border-zinc-400 px-4 py-3"
                            data-testid="offer-page-copy"
                        >
                            <span className="flex items-center gap-2 font-mono font-bold text-2xl tracking-widest">
                                <Tag size={20} weight="fill" className="text-pink-600" />
                                {coupon.code}
                            </span>
                            <span className="flex items-center gap-1 text-xs font-semibold text-zinc-500 uppercase">
                                <Copy size={14} /> Copy
                            </span>
                        </button>
                        <button onClick={claim} className="btn-primary w-full" data-testid="offer-page-claim">
                            Claim this offer
                        </button>
                        <ol className="text-sm text-zinc-600 space-y-1 list-decimal list-inside">
                            <li>Click "Claim this offer" (or go to Pricing).</li>
                            <li>Pick a package — the code is applied automatically.</li>
                            <li>Pay the discounted total by UPI / bank and submit the reference.</li>
                        </ol>
                    </div>
                </div>
            ) : (
                <div className="mt-8 border border-zinc-200 bg-white p-6">
                    <div className="font-display font-bold text-lg">No offer is running right now.</div>
                    <p className="text-sm text-zinc-600 mt-1">
                        Check back soon — new offers appear here and as a pop-up on the site. You can still start
                        with a free trial or pick a package.
                    </p>
                    <div className="flex gap-3 mt-4">
                        <Link to="/register" className="btn-primary">
                            Start free trial
                        </Link>
                        <Link to="/pricing" className="btn-secondary">
                            See pricing
                        </Link>
                    </div>
                </div>
            )}

            {packages.length > 0 && (
                <section className="mt-12">
                    <h2 className="font-display font-black text-2xl tracking-tighter mb-4">
                        {coupon ? "Package prices with this offer" : "Packages"}
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {packages.map((p) => {
                            const price = coupon
                                ? priceAfter(coupon, p)
                                : { base: Number(p.effective_price ?? p.price), final: Number(p.effective_price ?? p.price), applies: false };
                            const discounted = price.applies && price.final < price.base;
                            return (
                                <div key={p.id} className="border border-zinc-200 bg-white p-5" data-testid={`offer-pkg-${p.id}`}>
                                    <div className="font-display font-bold text-lg">{p.name}</div>
                                    <div className="text-xs text-zinc-500 font-mono">{p.card_limit} credits</div>
                                    <div className="mt-3 flex items-baseline gap-2">
                                        <span className="font-display font-black text-3xl tracking-tighter">
                                            ₹{price.final.toFixed(0)}
                                        </span>
                                        {discounted && (
                                            <span className="text-sm text-zinc-400 line-through">₹{price.base.toFixed(0)}</span>
                                        )}
                                    </div>
                                    {discounted && (
                                        <div className="mt-1 text-xs text-emerald-700 flex items-center gap-1">
                                            <CheckCircle size={14} weight="fill" /> You save ₹{(price.base - price.final).toFixed(0)}
                                        </div>
                                    )}
                                    {coupon && !price.applies && (
                                        <div className="mt-1 text-xs text-zinc-500">Offer not valid on this package</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}
